import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

interface ContractData {
  id: number;
  titulo: string | null;
  client_name: string | null;
  client_cnpj: string | null;
  client_email: string | null;
  contract_content: string | null;
  status: string;
  accepted_at: string | null;
  accepted_by_cpf: string | null;
  accepted_by_name: string | null;
}

export default function ContratoImportadoPage() {
  const { contractId } = useParams<{ contractId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contract, setContract] = useState<ContractData | null>(null);

  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (!contractId) return;
    loadContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const loadContract = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('contract-public', {
        body: { action: 'get_standalone', contract_id: Number(contractId) },
      });
      if (invokeErr) {
        setError((invokeErr as any)?.message ?? 'Erro ao carregar contrato.');
        return;
      }
      const parsed = data as { ok?: boolean; error?: string; contract?: ContractData };
      if (parsed?.error) {
        setError(parsed.error);
        return;
      }
      if (!parsed?.ok || !parsed?.contract) {
        setError('Contrato não encontrado.');
        return;
      }
      setContract(parsed.contract);
      if (parsed.contract.status === 'accepted') {
        setSubmitSuccess(true);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Erro ao carregar contrato.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setSubmitError(null);
    if (!accepted) {
      setSubmitError('Você precisa marcar que leu e aceita as cláusulas.');
      return;
    }
    const cleanName = name.trim();
    if (!cleanName) {
      setSubmitError('Informe seu nome completo.');
      return;
    }
    const cleanCpf = cpf.replace(/\D/g, '');
    if (cleanCpf.length < 11) {
      setSubmitError('Informe um CPF/CNPJ válido.');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('contract-public', {
        body: { action: 'accept_standalone', contract_id: Number(contractId), cpf: cleanCpf, name: cleanName },
      });
      if (invokeErr) {
        setSubmitError((invokeErr as any)?.message ?? 'Erro ao assinar contrato.');
        return;
      }
      const parsed = data as { ok?: boolean; error?: string; message?: string };
      if (parsed?.error) {
        setSubmitError(parsed.error);
        return;
      }
      if (!parsed?.ok) {
        setSubmitError('Falha ao assinar contrato.');
        return;
      }
      setSubmitSuccess(true);
      setContract((prev) =>
        prev ? { ...prev, status: 'accepted', accepted_at: new Date().toISOString(), accepted_by_cpf: cleanCpf, accepted_by_name: cleanName } : prev,
      );
    } catch (e: any) {
      setSubmitError(e?.message ?? 'Erro ao assinar contrato.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f6f4] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Carregando contrato...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f6f6f4] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-red-200 p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-red-50 text-red-600 rounded-xl mx-auto mb-4">
            <i className="ri-error-warning-line text-xl"></i>
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Contrato não encontrado</h2>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (submitSuccess || contract?.status === 'accepted') {
    return (
      <div className="min-h-screen bg-[#f6f6f4] py-12 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-2xl border border-green-200 p-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-green-50 text-green-600 rounded-full mx-auto mb-4">
            <i className="ri-check-line text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Contrato Assinado!</h1>
          <p className="text-gray-600 mb-4">
            O contrato <strong>{contract?.titulo || ''}</strong> foi assinado com sucesso.
          </p>
          {contract?.accepted_at && (
            <p className="text-sm text-gray-500">
              Assinado em: {formatDate(contract.accepted_at)} às{' '}
              {new Date(contract.accepted_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          {contract?.accepted_by_name && (
            <p className="text-sm text-gray-500 mt-1">Signatário: {contract.accepted_by_name}</p>
          )}
          {contract?.accepted_by_cpf && (
            <p className="text-sm text-gray-500 mt-1">CPF/CNPJ confirmado: {formatCPF(contract.accepted_by_cpf)}</p>
          )}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-500">Obrigado! Guarde esta página como comprovante de assinatura.</p>
            <a
              href="https://homeofficepremium.com"
              className="inline-flex items-center gap-2 mt-4 text-[#E8610A] hover:text-[#d45508] font-semibold text-sm transition-colors"
            >
              <i className="ri-home-line"></i>
              Voltar para o site
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f4] py-8 px-4">
      <div className="max-w-[800px] mx-auto">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
              <i className="ri-file-text-line text-lg"></i>
            </div>
            <div>
              <h1 className="font-bold text-gray-900">{contract?.titulo || 'Contrato'}</h1>
              {contract?.client_name && <p className="text-sm text-gray-500">{contract.client_name}</p>}
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold border rounded-full px-2.5 py-1 bg-amber-50 text-amber-700 border-amber-200 whitespace-nowrap">
            <i className="ri-time-line"></i>
            Aguardando assinatura
          </span>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-4">
          {contract?.contract_content ? (
            <div className="p-8 [&_*]:max-w-full" dangerouslySetInnerHTML={{ __html: contract.contract_content }} />
          ) : (
            <div className="p-8 text-center text-sm text-gray-500">Contrato sem conteúdo.</div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-pen-nib-line text-[#E8610A]"></i>
            Assinatura Digital
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Para assinar este contrato, informe seu <strong>nome completo</strong> e <strong>CPF/CNPJ</strong> e confirme
            que leu todas as cláusulas. A assinatura tem valor jurídico conforme Lei 14.063/2020.
          </p>

          {submitError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
              {submitError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome completo"
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8610A]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">CPF ou CNPJ</label>
              <input
                type="text"
                value={cpf}
                onChange={(e) => setCpf(formatCPF(e.target.value))}
                placeholder="000.000.000-00"
                maxLength={18}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#E8610A]"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-[#E8610A] border-gray-300 rounded focus:ring-[#E8610A]"
              />
              <span className="text-sm text-gray-700">
                Li e aceito todas as cláusulas do contrato acima. Entendo que este documento tem valor jurídico e me comprometo com as condições estabelecidas.
              </span>
            </label>

            <button
              onClick={handleAccept}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-xl hover:bg-[#d45508] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Processando...
                </>
              ) : (
                <>
                  <i className="ri-check-double-line"></i>
                  Assinar Contrato
                </>
              )}
            </button>
          </div>
        </div>

        <div className="text-center text-xs text-gray-400 pb-8">
          <p>Home Office Premium · Assinatura digital com validade jurídica (Lei 14.063/2020) · {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
}